import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Star, GraduationCap, Heart, Stethoscope, Shield, MessageCircle, Home, Clipboard, UserCheck, AlertTriangle, Activity, HandHeart, BookOpen, Settings } from 'lucide-react';

// Couleurs pour le DEAS - thème soins / santé
const theme = {
  primary: '#0D9488',      // Teal (soins)
  secondary: '#7C3AED',    // Violet
  accent1: '#059669',      // Vert émeraude (BC1)
  accent2: '#0891B2',      // Cyan (BC2)
  accent3: '#7C3AED',      // Violet (BC3)
  accent4: '#EA580C',      // Orange (BC4)
  accent5: '#2563EB',      // Bleu (BC5)
  background: '#F0FDFA',   // Fond teal très clair
  text: '#1E293B',
  success: '#10B981',
  error: '#EF4444',
};

const slides = [
  // INTRODUCTION
  {
    id: 1,
    title: "Réussir sa VAE DEAS",
    subtitle: "Diplôme d'État d'Aide-Soignant",
    description: "Les étapes clés pour réussir votre validation",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE DEAS ?",
    type: "benefits",
    items: [
      "Valoriser votre expérience dans le domaine des soins",
      "Obtenir un diplôme d'État reconnu niveau 4 (Bac)",
      "Sécuriser votre parcours professionnel",
      "Accéder à un métier porteur avec de nombreux débouchés",
      "Évoluer vers des fonctions d'infirmier(ère)"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience conseillé (1607h cumulées)",
      "Activités en lien avec l'accompagnement et les soins",
      "Expérience dans le secteur sanitaire, social ou médico-social",
      "Fonctions d'aide-soignant, auxiliaire de vie, agent de service..."
    ]
  },
  {
    id: 4,
    title: "Les 7 étapes de la VAE",
    type: "steps",
    items: [
      { num: 1, text: "Vérifier son éligibilité" },
      { num: 2, text: "Déposer le Livret 1" },
      { num: 3, text: "Rédiger le Livret 2" },
      { num: 4, text: "Se faire accompagner" },
      { num: 5, text: "Déposer le dossier" },
      { num: 6, text: "Passer l'oral" },
      { num: 7, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Les 5 blocs de compétences",
    type: "blocs-overview",
    blocs: [
      { num: 1, title: "Accompagnement et soins de la personne", color: theme.accent1 },
      { num: 2, title: "Évaluation de l'état clinique et soins adaptés", color: theme.accent2 },
      { num: 3, title: "Information et accompagnement", color: theme.accent3 },
      { num: 4, title: "Entretien de l'environnement", color: theme.accent4 },
      { num: 5, title: "Travail en équipe pluri-professionnelle", color: theme.accent5 }
    ]
  },

  // BLOC 1 - Accompagnement et soins
  {
    id: 6,
    title: "Bloc 1 – Accompagnement et soins",
    subtitle: "de la personne dans les activités de sa vie quotidienne et sociale",
    type: "bloc-title",
    color: theme.accent1,
    icon: "heart"
  },
  {
    id: 7,
    title: "Compétence 1 – Accompagner les personnes",
    subtitle: "dans les actes essentiels de la vie quotidienne et sociale",
    type: "competence",
    color: theme.accent1,
    items: [
      "Rechercher et analyser les informations sur les habitudes de vie, la culture, les choix de la personne",
      "Évaluer le degré d'autonomie, les fragilités, handicaps, ressources et capacités de la personne",
      "Identifier et évaluer les besoins en tenant compte de la situation singulière",
      "Mettre en œuvre l'accompagnement personnalisé dans les actes essentiels",
      "Mettre en œuvre les activités d'accompagnement à la vie sociale",
      "Mobiliser les ressources de la personne et adapter son accompagnement",
      "Adapter la communication avec la personne, les aidants et professionnels",
      "Aider, accompagner et conforter les aidants dans leur rôle"
    ]
  },
  {
    id: 8,
    title: "Compétence 2 – Identifier les situations à risque",
    subtitle: "mettre en œuvre les actions de prévention et les évaluer",
    type: "competence",
    color: theme.accent1,
    items: [
      "Identifier les signes de fragilités ou vulnérabilités potentielles",
      "Repérer les situations à risque, notamment de maltraitance, et alerter",
      "Proposer des mesures de prévention en coopération pluri-professionnelle",
      "Évaluer les mesures mises en œuvre et les réajuster si nécessaire"
    ]
  },

  // BLOC 2 - Évaluation de l'état clinique
  {
    id: 9,
    title: "Bloc 2 – Évaluation de l'état clinique",
    subtitle: "et mise en œuvre de soins adaptés en collaboration",
    type: "bloc-title",
    color: theme.accent2,
    icon: "stethoscope"
  },
  {
    id: 10,
    title: "Compétence 3 – Évaluer l'état clinique",
    subtitle: "d'une personne à tout âge de la vie pour adapter sa prise en soins",
    type: "competence",
    color: theme.accent2,
    items: [
      "Rechercher et analyser les informations pour identifier l'état général de la personne",
      "Identifier les signes de changements de l'état clinique",
      "Évaluer la douleur, mesurer les paramètres vitaux avec les outils adaptés",
      "Évaluer l'état clinique par un raisonnement clinique",
      "Transmettre à l'oral et à l'écrit les informations pour la prise en soins",
      "Discerner le caractère urgent d'une situation et alerter"
    ]
  },
  {
    id: 11,
    title: "Compétence 4 – Mettre en œuvre des soins adaptés",
    subtitle: "à l'état clinique de la personne",
    type: "competence",
    color: theme.accent2,
    items: [
      "Rechercher les informations sur les précautions particulières à respecter",
      "Analyser les informations et prioriser les activités de soin",
      "Identifier et appliquer les règles de bonnes pratiques",
      "Organiser l'activité de soin et adapter les modalités de réalisation",
      "Mettre en œuvre des soins personnalisés en collaboration avec l'infirmier(ère)",
      "Expliquer le soin réalisé et l'objectif du geste",
      "Évaluer la qualité du soin réalisé et réajuster son intervention"
    ]
  },
  {
    id: 12,
    title: "Compétence 5 – Accompagner la personne",
    subtitle: "dans son installation et ses déplacements",
    type: "competence",
    color: theme.accent2,
    items: [
      "Évaluer les capacités d'une personne à réaliser un déplacement",
      "Effectuer une guidance verbale et non verbale, identifier les ressources adaptées",
      "Aménager un espace sécurisé et sécurisant",
      "Utiliser les dispositifs d'aide à la mobilisation en sécurité",
      "Identifier les situations à risque pour la personne et soi-même, alerter",
      "Installer la personne en tenant compte de la singularité de la situation"
    ]
  },

  // BLOC 3 - Information et accompagnement
  {
    id: 13,
    title: "Bloc 3 – Information et accompagnement",
    subtitle: "des personnes, de leur entourage, des professionnels et apprenants",
    type: "bloc-title",
    color: theme.accent3,
    icon: "message"
  },
  {
    id: 14,
    title: "Compétence 6 – Établir une communication adaptée",
    subtitle: "pour informer et accompagner la personne et son entourage",
    type: "competence",
    color: theme.accent3,
    items: [
      "Écouter la personne et son entourage en prenant en compte la communication verbale et non verbale",
      "Mettre en œuvre des soins relationnels adaptés à des publics diversifiés",
      "Identifier les informations pertinentes à transmettre en collaboration avec l'équipe",
      "Apporter des conseils en éducation à la santé et hygiène de vie",
      "Permettre à la personne d'exprimer ses besoins et proposer des modalités adaptées",
      "Apporter des informations pratiques lors de l'accueil dans le service"
    ]
  },
  {
    id: 15,
    title: "Compétence 7 – Informer et former",
    subtitle: "les pairs, les personnes en formation et autres professionnels",
    type: "competence",
    color: theme.accent3,
    items: [
      "Identifier les informations à apporter aux personnes en formation lors de l'accueil",
      "Rechercher les informations sur les besoins d'apprentissage",
      "Accompagner les activités et le développement des compétences",
      "Identifier les acquis et écarts et adapter son encadrement",
      "Évaluer les compétences avec les outils de suivi de l'apprentissage",
      "Accompagner la réflexion sur les apprentissages"
    ]
  },

  // BLOC 4 - Entretien de l'environnement
  {
    id: 16,
    title: "Bloc 4 – Entretien de l'environnement",
    subtitle: "immédiat de la personne et des matériels liés aux activités",
    type: "bloc-title",
    color: theme.accent4,
    icon: "home"
  },
  {
    id: 17,
    title: "Compétence 8 – Techniques d'entretien",
    subtitle: "des locaux et du matériel avec prévention des risques",
    type: "competence",
    color: theme.accent4,
    items: [
      "Identifier et choisir le matériel et produits appropriés",
      "Utiliser les techniques de nettoyage et désinfection pour lutter contre les contaminations",
      "Appliquer les principes d'hygiène pour prévenir les infections",
      "Respecter les circuits du linge, matériels, dispositifs médicaux et déchets",
      "Respecter les circuits et procédures liés à l'alimentation",
      "Adapter ses actions en fonction du lieu d'intervention"
    ]
  },
  {
    id: 18,
    title: "Compétence 9 – Repérer et traiter les anomalies",
    subtitle: "et dysfonctionnements liés à l'entretien",
    type: "competence",
    color: theme.accent4,
    items: [
      "Évaluer l'efficacité des opérations, identifier les anomalies et réajuster",
      "Repérer les anomalies relatives à l'entretien de l'environnement et alerter",
      "Vérifier le bon fonctionnement des matériels liés aux activités de soins"
    ]
  },

  // BLOC 5 - Travail en équipe
  {
    id: 19,
    title: "Bloc 5 – Travail en équipe pluri-professionnelle",
    subtitle: "et traitement des informations liées aux soins et à la qualité",
    type: "bloc-title",
    color: theme.accent5,
    icon: "users"
  },
  {
    id: 20,
    title: "Compétence 10 – Rechercher, traiter et transmettre",
    subtitle: "les données pour assurer la continuité et traçabilité des soins",
    type: "competence",
    color: theme.accent5,
    items: [
      "Rechercher, organiser et hiérarchiser les informations pour la prise en soins",
      "Transcrire et transmettre les informations et observations relatives à la personne",
      "Analyser les situations et relayer les informations en lien avec le projet de soins"
    ]
  },
  {
    id: 21,
    title: "Compétence 11 – Organiser son activité",
    subtitle: "coopérer en équipe et améliorer sa pratique",
    type: "competence",
    color: theme.accent5,
    items: [
      "Identifier son champ d'intervention au regard des fonctions de chaque professionnel",
      "Organiser sa propre activité en s'inscrivant dans la planification de l'équipe",
      "Organiser et prioriser son activité selon les besoins et le contexte",
      "Apporter une contribution au projet de soins, de vie et institutionnel",
      "Repérer, signaler et déclarer les événements indésirables",
      "Contribuer à l'évaluation des pratiques et à la démarche qualité",
      "Évaluer sa pratique et identifier ses besoins en formation",
      "Proposer des actions d'amélioration de la qualité et prévention des risques"
    ]
  },

  // CONSEILS ET ORAL
  {
    id: 22,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Décrire des situations de soins concrètes",
      "Analyser vos pratiques professionnelles",
      "Justifier vos choix et vos gestes",
      "Montrer votre posture d'aide-soignant(e)"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« Je fais la toilette des patients »",
      right: "« J'accompagne M. Dupont dans sa toilette en respectant son autonomie et sa pudeur. J'observe l'état de sa peau et je signale à l'infirmière une rougeur au niveau du sacrum. »"
    }
  },
  {
    id: 23,
    title: "L'oral devant le jury",
    type: "oral",
    details: [
      { label: "Durée totale", value: "1 heure" },
      { label: "Présentation personnelle", value: "10 minutes" },
      { label: "Échanges avec le jury", value: "50 minutes" }
    ],
    note: "Questions sur vos situations de soins, votre positionnement professionnel et votre connaissance du référentiel."
  },
  {
    id: 24,
    title: "Décision du jury",
    type: "decision",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre diplôme DEAS !" },
      { status: "partial", text: "Validation partielle", desc: "Blocs de compétences à compléter" },
      { status: "retry", text: "Ajournement", desc: "Possibilité de représenter le dossier après travail complémentaire" }
    ]
  },
  {
    id: 25,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Aide-soignant(e) en hôpital",
      "Aide-soignant(e) en EHPAD",
      "Aide-soignant(e) à domicile (SSIAD)",
      "Aide-soignant(e) en clinique",
      "Aide-soignant(e) en HAD",
      "Évolution vers IFSI (infirmier)"
    ]
  },
  {
    id: 26,
    title: "En résumé",
    type: "summary",
    points: [
      "Le DEAS valorise vos compétences en soins",
      "La VAE exige rigueur et réflexion sur la pratique",
      "Une préparation méthodique favorise la réussite"
    ]
  }
];

const getIcon = (iconName, size = 50) => {
  switch (iconName) {
    case 'heart': return <Heart size={size} />;
    case 'stethoscope': return <Stethoscope size={size} />;
    case 'message': return <MessageCircle size={size} />;
    case 'home': return <Home size={size} />;
    case 'users': return <Users size={size} />;
    default: return <Award size={size} />;
  }
};

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent2} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', height: '100px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 2rem', border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <Stethoscope size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.75rem', color: 'white' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', opacity: 0.95 }}>{slide.subtitle}</p>
          <p style={{ fontSize: '1rem', opacity: 0.85 }}>{slide.description}</p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <Star size={20} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.success}`
              }}>
                <CheckCircle size={22} style={{ color: theme.success, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', maxWidth: '750px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                  color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                }}>{item.num}</div>
                <span style={{ fontSize: '0.85rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "blocs-overview":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.blocs.map((bloc, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: bloc.color, borderRadius: '0.75rem', color: 'white'
              }}>
                <div style={{
                  width: '45px', height: '45px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.25rem', flexShrink: 0
                }}>{bloc.num}</div>
                <h4 style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{bloc.title}</h4>
              </div>
            ))}
          </div>
        </div>
      );

    case "bloc-title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: slide.color,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
          }}>
            {getIcon(slide.icon, 40)}
          </div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{slide.title}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>{slide.subtitle}</p>
        </div>
      );

    case "competence":
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: slide.color, color: 'white', padding: '1rem 1.5rem', 
            borderRadius: '0.75rem 0.75rem 0 0', marginBottom: '0'
          }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ fontSize: '0.85rem', opacity: 0.9 }}>{slide.subtitle}</p>}
          </div>
          <div style={{ 
            background: 'white', borderRadius: '0 0 0.75rem 0.75rem', 
            padding: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxHeight: '320px', overflowY: 'auto'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.5rem 0',
                borderBottom: idx < slide.items.length - 1 ? `1px solid #eee` : 'none'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%', background: slide.color,
                  marginTop: '0.5rem', flexShrink: 0
                }} />
                <span style={{ fontSize: '0.85rem', color: theme.text, lineHeight: '1.4' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
              }}>
                <Target size={18} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '650px', margin: '0 auto', padding: '1rem',
            background: '#f8f9fa', borderRadius: '0.75rem', border: `2px solid ${theme.accent2}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: theme.primary, textAlign: 'center', fontSize: '0.95rem' }}>
              {slide.example.title}
            </p>
            <div style={{ padding: '0.5rem 0.75rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: theme.error, fontWeight: 'bold' }}>✗</span>
              <span style={{ color: theme.error, fontSize: '0.9rem' }}>{slide.example.wrong}</span>
            </div>
            <div style={{ padding: '0.5rem 0.75rem', background: '#e8f5e9', borderRadius: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ color: theme.success, fontWeight: 'bold' }}>✓</span>
              <span style={{ color: theme.success, fontSize: '0.8rem' }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "oral":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '500px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {slide.details.map((detail, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem',
                background: theme.background, borderRadius: '0.5rem', marginBottom: '0.75rem'
              }}>
                <span style={{ fontWeight: 'bold', color: theme.text }}>{detail.label}</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>{detail.value}</span>
              </div>
            ))}
            <div style={{ padding: '0.75rem', background: theme.accent2 + '20', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: theme.text, fontStyle: 'italic' }}>{slide.note}</p>
            </div>
          </div>
        </div>
      );

    case "decision":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                background: decision.status === 'success' ? '#e8f5e9' : decision.status === 'partial' ? '#fff3e0' : '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#ff9800' : theme.error}`
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#e65100' : theme.error }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Heart size={18} style={{ color: theme.primary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent2} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', color: 'white' }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '550px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '0.75rem'
              }}>
                <CheckCircle size={20} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '0.95rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.9)', borderRadius: '0.75rem' }}>
            <p style={{ color: theme.primary, fontWeight: 'bold' }}>
              Lancez-vous dans l'aventure VAE DEAS avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const DEASPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ borderRadius: '1rem', overflow: 'hidden', background: theme.background, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} data-testid="deas-presentation">
      <div style={{ minHeight: '450px', padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.accent2}20`, background: 'white' }}>
        <button onClick={prevSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: `1px solid ${theme.accent2}`, borderRadius: '0.5rem', background: 'white', cursor: 'pointer', color: theme.text, fontWeight: '500' }} data-testid="prev-slide-btn">
          <ChevronLeft size={18} /><span>Précédent</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: theme.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }} data-testid="play-pause-btn">
            {isPlaying ? '⏸' : <Play size={16} />}
          </button>
          <span style={{ fontWeight: 'bold', color: theme.primary, fontSize: '0.95rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: theme.primary, cursor: 'pointer', color: 'white', fontWeight: '500' }} data-testid="next-slide-btn">
          <span>Suivant</span><ChevronRight size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', padding: '0.75rem', background: 'white', flexWrap: 'wrap' }}>
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setCurrentSlide(idx)} style={{ width: '8px', height: '8px', borderRadius: '50%', border: 'none', background: idx === currentSlide ? theme.primary : theme.accent2, cursor: 'pointer', opacity: idx === currentSlide ? 1 : 0.4 }} data-testid={`slide-dot-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default DEASPresentation;
